import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGxZones } from '../hooks/useGxZones';
import { useGxSubscription } from '../hooks/useGxSubscription';
import { GxFeedItem, getGxConfig, updateGxConfig, getEvents } from '../api/guestExperience';
import { EmergencyAlertButton } from '../components/gx/EmergencyAlertButton';
import { QuickActionPanel } from '../components/gx/QuickActionPanel';
import { LiveNoteInput } from '../components/gx/LiveNoteInput';
import { ZoneManager } from '../components/gx/ZoneManager';
import { ActionManager } from '../components/gx/ActionManager';

interface EventItem {
  id: string;
  name: string;
}

const LEGACY_BALL_ID = 'e1000000-0000-0000-0000-000000000003';

const pageStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1rem',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.5rem',
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    color: '#666',
    fontSize: '0.875rem',
    margin: 0,
  },
  eventSelector: {
    marginBottom: '1rem',
  },
  eventLabel: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#999',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.35rem',
  },
  eventSelect: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    backgroundColor: '#fff',
  },
  viewFeedLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 1rem',
    backgroundColor: '#e8f5e9',
    color: '#1a6b3c',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    marginBottom: '1rem',
    textDecoration: 'none',
  },
  subscriptionBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  subscribedBanner: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  unsubscribedBanner: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  subscribeButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  section: {
    marginBottom: '1rem',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#999',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
  },
  notePosted: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '6px',
    fontSize: '0.8rem',
    marginBottom: '0.5rem',
  },
  collapsibleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#333',
    width: '100%',
    textAlign: 'left' as const,
  },
  collapsibleContent: {
    padding: '1rem 0',
  },
  muteToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.6rem 1rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    marginBottom: '1rem',
  },
  mutedStyle: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  activeStyle: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
};

export function GuestExperienceDashboardPage({ eventIdOverride }: { eventIdOverride?: string } = {}) {
  const navigate = useNavigate();
  const { eventId: paramEventId } = useParams<{ eventId: string }>();

  const [events, setEvents] = useState<EventItem[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>(eventIdOverride || paramEventId || '');
  const [eventsLoading, setEventsLoading] = useState(true);
  const [recentNote, setRecentNote] = useState<GxFeedItem | null>(null);
  const [zonesOpen, setZonesOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [muteLoading, setMuteLoading] = useState(true);
  const [muteToggling, setMuteToggling] = useState(false);

  // Fetch GX config (mute status) on mount
  useEffect(() => {
    getGxConfig()
      .then((config) => {
        setMuted(config.muted);
      })
      .catch(() => {
        // Use default on error
      })
      .finally(() => setMuteLoading(false));
  }, []);

  const handleMuteToggle = async () => {
    setMuteToggling(true);
    try {
      const updated = await updateGxConfig({ muted: !muted });
      setMuted(updated.muted);
    } catch {
      // API will reject if not Admin — silently ignore
    } finally {
      setMuteToggling(false);
    }
  };

  // Fetch events on mount
  useEffect(() => {
    async function fetchEvents() {
      try {
        const data = await getEvents();
        setEvents(data);
        // If override or param provided, keep it; otherwise default to Legacy Ball or first event
        if (!eventIdOverride && !paramEventId) {
          const legacyBall = data.find((e) => e.id === LEGACY_BALL_ID);
          if (legacyBall) {
            setSelectedEventId(legacyBall.id);
          } else if (data.length > 0) {
            setSelectedEventId(data[0].id);
          }
        }
      } catch {
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }
    }
    fetchEvents();
  }, [eventIdOverride, paramEventId]);

  const { subscription, isLoading: subLoading, subscribe } = useGxSubscription(selectedEventId);
  const { zones } = useGxZones(selectedEventId);

  const isSubscribed = subscription !== null;

  const handleSubscribe = async () => {
    try {
      await subscribe();
    } catch {
      // Subscription error handled silently
    }
  };

  const handleNoteCreated = useCallback((item: GxFeedItem) => {
    setRecentNote(item);
    setTimeout(() => setRecentNote(null), 3000);
  }, []);

  if (eventsLoading) {
    return (
      <div style={pageStyles.container}>
        <p style={pageStyles.loading}>Loading...</p>
      </div>
    );
  }

  if (!selectedEventId) {
    return (
      <div style={pageStyles.container}>
        <p style={pageStyles.loading}>No events available.</p>
      </div>
    );
  }

  return (
    <div style={pageStyles.container}>
      {/* Header */}
      <div style={pageStyles.header}>
        <h1 style={pageStyles.title}>GX Dashboard</h1>
        <p style={pageStyles.subtitle}>Post notes, alerts, and quick actions</p>
      </div>

      {/* Event Selector */}
      <div style={pageStyles.eventSelector}>
        <label style={pageStyles.eventLabel}>Event</label>
        <select
          style={pageStyles.eventSelect}
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          {events.map((evt) => (
            <option key={evt.id} value={evt.id}>
              {evt.name}
            </option>
          ))}
        </select>
      </div>

      {/* View Live Feed Link */}
      <button
        style={pageStyles.viewFeedLink}
        onClick={() => navigate('/kongosa-box')}
      >
        📡 View Live Feed →
      </button>

      {/* Mute Toggle */}
      {!muteLoading && (
        <button
          style={{
            ...pageStyles.muteToggle,
            ...(muted ? pageStyles.mutedStyle : pageStyles.activeStyle),
          }}
          onClick={handleMuteToggle}
          disabled={muteToggling}
        >
          {muted ? '🔇 Feed Muted' : '🔊 Feed Active'}
        </button>
      )}

      {/* Subscription Status */}
      {subLoading ? (
        <div style={{ ...pageStyles.subscriptionBanner, backgroundColor: '#f5f5f5', color: '#999' }}>
          Checking subscription...
        </div>
      ) : isSubscribed ? (
        <div style={{ ...pageStyles.subscriptionBanner, ...pageStyles.subscribedBanner }}>
          <span>✓ Subscribed to GX Feed</span>
        </div>
      ) : (
        <div style={{ ...pageStyles.subscriptionBanner, ...pageStyles.unsubscribedBanner }}>
          <span>Not subscribed</span>
          <button style={pageStyles.subscribeButton} onClick={handleSubscribe}>
            Subscribe to GX Feed
          </button>
        </div>
      )}

      {/* Emergency Alert Button */}
      <div style={pageStyles.section}>
        <div style={pageStyles.sectionLabel}>Emergency</div>
        <EmergencyAlertButton
          eventId={selectedEventId}
          zones={zones}
          selectedZoneId={undefined}
        />
      </div>

      {/* Quick Action Panel */}
      <div style={pageStyles.section}>
        <div style={pageStyles.sectionLabel}>Quick Actions</div>
        <QuickActionPanel
          eventId={selectedEventId}
          zones={zones}
          selectedZoneId={undefined}
        />
      </div>

      {/* Note Input */}
      <div style={pageStyles.section}>
        <div style={pageStyles.sectionLabel}>Post Note</div>
        {recentNote && (
          <div style={pageStyles.notePosted}>
            ✓ Note posted: &quot;{recentNote.message}&quot;
          </div>
        )}
        <LiveNoteInput
          eventId={selectedEventId}
          zones={zones}
          onNoteCreated={handleNoteCreated}
        />
      </div>

      {/* Collapsible Manage Zones */}
      <div style={pageStyles.section}>
        <button
          style={pageStyles.collapsibleHeader}
          onClick={() => setZonesOpen(!zonesOpen)}
        >
          <span>{zonesOpen ? '▼' : '▶'}</span>
          <span>⚙️ Manage Zones</span>
        </button>
        {zonesOpen && (
          <div style={pageStyles.collapsibleContent}>
            <ZoneManager eventId={selectedEventId} />
          </div>
        )}
      </div>

      {/* Collapsible Manage Quick Actions */}
      <div style={pageStyles.section}>
        <button
          style={pageStyles.collapsibleHeader}
          onClick={() => setActionsOpen(!actionsOpen)}
        >
          <span>{actionsOpen ? '▼' : '▶'}</span>
          <span>⚙️ Manage Quick Actions</span>
        </button>
        {actionsOpen && (
          <div style={pageStyles.collapsibleContent}>
            <ActionManager eventId={selectedEventId} />
          </div>
        )}
      </div>
    </div>
  );
}
